import React, { Suspense, lazy } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { ScrollToTop } from "./components/ScrollToTop";
import SkipLink from "./components/SkipLink";

// Loading component for lazy-loaded routes
const PageLoader = () => (
  <div style={{ 
    display: 'flex', 
    justifyContent: 'center', 
    alignItems: 'center', 
    minHeight: '100vh',
    background: '#0B1825'
  }}>
    <div style={{ color: '#fff', fontSize: '18px' }}>Loading...</div>
  </div>
);

// Eager load only critical pages (HomePage for fast initial load)
import { HomePage } from "./pages/HomePage";

// Lazy load all other pages for code splitting
const AboutPage = lazy(() => import("./pages/AboutPage").then(m => ({ default: m.AboutPage })));
const MiningPage = lazy(() => import("./pages/MiningPage").then(m => ({ default: m.MiningPage })));
const IndustrialsPage = lazy(() => import("./pages/IndustrialsPage").then(m => ({ default: m.IndustrialsPage })));
const CasePage = lazy(() => import("./pages/CasePage").then(m => ({ default: m.CasePage })));
const CaseLibraryPage = lazy(() => import("./pages/CaseLibraryPage").then(m => ({ default: m.CaseLibraryPage })));
const ChallengePage = lazy(() => import("./pages/ChallengePage").then(m => ({ default: m.ChallengePage })));
const SubcategoryPage = lazy(() => import("./pages/SubcategoryPage").then(m => ({ default: m.SubcategoryPage })));
const HowWeWorkPage = lazy(() => import("./pages/HowWeWorkPage").then(m => ({ default: m.HowWeWorkPage })));
const MarketReportPage = lazy(() => import("./pages/MarketReportPage").then(m => ({ default: m.MarketReportPage })));
const AIStrategyPage = lazy(() => import("./pages/AIStrategyPage").then(m => ({ default: m.AIStrategyPage })));
const DataTechnicalFeasibilityPage = lazy(() => import("./pages/DataTechnicalFeasibilityPage").then(m => ({ default: m.DataTechnicalFeasibilityPage })));
const VendorTechnologyAdvisoryPage = lazy(() => import("./pages/VendorTechnologyAdvisoryPage"));
const ChangeManagementPage = lazy(() => import("./pages/ChangeManagementPage"));
const ExecAlignmentWorkshopsPage = lazy(() => import("./pages/ExecAlignmentWorkshopsPage"));
const ManagerEnablementBootcampsPage = lazy(() => import("./pages/ManagerEnablementBootcampsPage"));
const FrontlineUpskillingProgramsPage = lazy(() => import("./pages/FrontlineUpskillingProgramsPage"));
const DigitalChampionsNetworkPage = lazy(() => import("./pages/DigitalChampionsNetworkPage"));
const GovernanceRiskManagementPage = lazy(() => import("./pages/GovernanceRiskManagementPage"));
const AIProgramPage = lazy(() => import("./pages/AIProgramPage"));
const ModelDesignArchitecturePage = lazy(() => import("./pages/ModelDesignArchitecturePage"));
const ProofOfConceptPilotsPage = lazy(() => import("./pages/ProofOfConceptPilotsPage"));
const ProductizationScalingPage = lazy(() => import("./pages/ProductizationScalingPage"));
const MLOpsMonitoringPage = lazy(() => import("./pages/MLOpsMonitoringPage"));
const AIApplicationsPage = lazy(() => import("./pages/AIApplicationsPage"));
const DataInfrastructureArchitecturePage = lazy(() => import("./pages/DataInfrastructureArchitecturePage"));
const DataPipelinesIntegrationPage = lazy(() => import("./pages/DataPipelinesIntegrationPage"));
const APIsInteroperabilityPage = lazy(() => import("./pages/APIsInteroperabilityPage"));
const DataGovernanceQualityPage = lazy(() => import("./pages/DataGovernanceQualityPage"));
const VisualizationInsightsPage = lazy(() => import("./pages/VisualizationInsightsPage"));
const WhatWeDoPage = lazy(() => import("./pages/WhatWeDoPage").then(m => ({ default: m.WhatWeDoPage })));
const IndustrialsHowWeWorkPage = lazy(() => import("./pages/IndustrialsHowWeWorkPage").then(m => ({ default: m.IndustrialsHowWeWorkPage })));
const IndustrialsChallengesPage = lazy(() => import("./pages/IndustrialsChallengesPage").then(m => ({ default: m.IndustrialsChallengesPage })));

// Industrials Pages - Lazy loaded
const IndustrialsAIProgramPage = lazy(() => import("./pages/IndustrialsAIProgramPage"));
const IndustrialsAIStrategyPage = lazy(() => import("./pages/IndustrialsAIStrategyPage"));
const IndustrialsVendorTechnologyAdvisoryPage = lazy(() => import("./pages/IndustrialsVendorTechnologyAdvisoryPage"));
const IndustrialsChangeManagementPage = lazy(() => import("./pages/IndustrialsChangeManagementPage"));
const IndustrialsExecAlignmentWorkshopsPage = lazy(() => import("./pages/IndustrialsExecAlignmentWorkshopsPage"));
const IndustrialsManagerEnablementBootcampsPage = lazy(() => import("./pages/IndustrialsManagerEnablementBootcampsPage"));
const IndustrialsFrontlineUpskillingProgramsPage = lazy(() => import("./pages/IndustrialsFrontlineUpskillingProgramsPage"));
const IndustrialsDigitalChampionsNetworkPage = lazy(() => import("./pages/IndustrialsDigitalChampionsNetworkPage"));
const IndustrialsGovernanceRiskManagementPage = lazy(() => import("./pages/IndustrialsGovernanceRiskManagementPage"));
const IndustrialsModelDesignArchitecturePage = lazy(() => import("./pages/IndustrialsModelDesignArchitecturePage"));
const IndustrialsProofOfConceptPilotsPage = lazy(() => import("./pages/IndustrialsProofOfConceptPilotsPage"));
const IndustrialsProductizationScalingPage = lazy(() => import("./pages/IndustrialsProductizationScalingPage"));
const IndustrialsMLOpsMonitoringPage = lazy(() => import("./pages/IndustrialsMLOpsMonitoringPage"));
const IndustrialsAIApplicationsPage = lazy(() => import("./pages/IndustrialsAIApplicationsPage"));
const IndustrialsDataInfrastructureArchitecturePage = lazy(() => import("./pages/IndustrialsDataInfrastructureArchitecturePage"));
const IndustrialsDataPipelinesIntegrationPage = lazy(() => import("./pages/IndustrialsDataPipelinesIntegrationPage"));
const IndustrialsAPIsInteroperabilityPage = lazy(() => import("./pages/IndustrialsAPIsInteroperabilityPage"));
const IndustrialsDataGovernanceQualityPage = lazy(() => import("./pages/IndustrialsDataGovernanceQualityPage"));
const IndustrialsVisualizationInsightsPage = lazy(() => import("./pages/IndustrialsVisualizationInsightsPage"));
const SelectedCasePage = lazy(() => import("./pages/SelectedCasePage").then(m => ({ default: m.SelectedCasePage })));
const IndustryPage = lazy(() => import("./pages/IndustryPage").then(m => ({ default: m.IndustryPage })));
const OfferingsPage = lazy(() => import("./pages/OfferingsPage").then(m => ({ default: m.OfferingsPage })));
const CapabilitiesPage = lazy(() => import("./pages/CapabilitiesPage").then(m => ({ default: m.CapabilitiesPage })));
const BookMeetingPage = lazy(() => import("./pages/BookMeetingPage").then(m => ({ default: m.BookMeetingPage })));

function App() {
  return (
    <div className="App">
      <BrowserRouter>
          <SkipLink />
          <ScrollToTop />
          <Suspense fallback={<PageLoader />}>
            <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/what-we-do" element={<WhatWeDoPage />} />
          <Route path="/mining" element={<MiningPage />} />
          <Route path="/industrials" element={<IndustrialsPage />} />
          <Route path="/:vertical/cases/:slug" element={<CasePage />} />
          <Route path="/:vertical/case-library" element={<CaseLibraryPage />} />
          <Route path="/:vertical/case-library/:caseSlug" element={<CaseLibraryPage />} />
          <Route path="/mining/challenges" element={<ChallengePage />} />
          <Route path="/mining/challenges/:slug" element={<ChallengePage />} />
          <Route path="/industrials/challenges" element={<IndustrialsChallengesPage />} />
          <Route path="/industrials/challenges/:slug" element={<IndustrialsChallengesPage />} />
          <Route path="/:vertical/services/:slug" element={<SubcategoryPage />} />
          <Route path="/mining/MineForward-AI-Program" element={<HowWeWorkPage />} />
          <Route path="/industrials/FactoryForward-AI-Program" element={<IndustrialsHowWeWorkPage />} />
          <Route path="/:vertical/how-we-work" element={<HowWeWorkPage />} />
          <Route path="/insights/ai-in-mining-2025" element={<MarketReportPage />} />
          <Route path="/mining/services/ai-program" element={<AIProgramPage />} />
          <Route path="/mining/services/strategy" element={<AIStrategyPage />} />
          <Route path="/mining/services/vendor-technology-advisory" element={<VendorTechnologyAdvisoryPage />} />
          <Route path="/mining/services/change-management" element={<ChangeManagementPage />} />
          <Route path="/mining/services/executive-alignment-workshops" element={<ExecAlignmentWorkshopsPage />} />
          <Route path="/mining/services/manager-enablement-bootcamps" element={<ManagerEnablementBootcampsPage />} />
          <Route path="/mining/services/frontline-upskilling-programs" element={<FrontlineUpskillingProgramsPage />} />
          <Route path="/mining/services/digital-champions-network" element={<DigitalChampionsNetworkPage />} />
          <Route path="/mining/services/governance-risk-management" element={<GovernanceRiskManagementPage />} />
          <Route path="/mining/services/model-design-architecture" element={<ModelDesignArchitecturePage />} />
          <Route path="/mining/services/proof-of-concept-pilots" element={<ProofOfConceptPilotsPage />} />
          <Route path="/mining/services/productization-scaling" element={<ProductizationScalingPage />} />
          <Route path="/mining/services/mlops-model-monitoring" element={<MLOpsMonitoringPage />} />
          <Route path="/mining/services/ai-applications-use-cases" element={<AIApplicationsPage />} />
          <Route path="/mining/data-engineering/data-infrastructure-architecture" element={<DataInfrastructureArchitecturePage />} />
          <Route path="/mining/data-engineering/data-pipelines-integration" element={<DataPipelinesIntegrationPage />} />
          <Route path="/mining/data-engineering/apis-interoperability" element={<APIsInteroperabilityPage />} />
          <Route path="/mining/data-engineering/data-governance-quality" element={<DataGovernanceQualityPage />} />
          <Route path="/mining/data-engineering/visualization-insights" element={<VisualizationInsightsPage />} />
          
          {/* Industrials Routes */}
          <Route path="/industrials/services/ai-program" element={<IndustrialsAIProgramPage />} />
          <Route path="/industrials/services/strategy" element={<IndustrialsAIStrategyPage />} />
          <Route path="/industrials/services/vendor-technology-advisory" element={<IndustrialsVendorTechnologyAdvisoryPage />} />
          <Route path="/industrials/services/change-management" element={<IndustrialsChangeManagementPage />} />
          <Route path="/industrials/services/executive-alignment-workshops" element={<IndustrialsExecAlignmentWorkshopsPage />} />
          <Route path="/industrials/services/manager-enablement-bootcamps" element={<IndustrialsManagerEnablementBootcampsPage />} />
          <Route path="/industrials/services/frontline-upskilling-programs" element={<IndustrialsFrontlineUpskillingProgramsPage />} />
          <Route path="/industrials/services/digital-champions-network" element={<IndustrialsDigitalChampionsNetworkPage />} />
          <Route path="/industrials/services/governance-risk-management" element={<IndustrialsGovernanceRiskManagementPage />} />
          <Route path="/industrials/services/model-design-architecture" element={<IndustrialsModelDesignArchitecturePage />} />
          <Route path="/industrials/services/proof-of-concept-pilots" element={<IndustrialsProofOfConceptPilotsPage />} />
          <Route path="/industrials/services/productization-scaling" element={<IndustrialsProductizationScalingPage />} />
          <Route path="/industrials/services/mlops-model-monitoring" element={<IndustrialsMLOpsMonitoringPage />} />
          <Route path="/industrials/services/ai-applications-use-cases" element={<IndustrialsAIApplicationsPage />} />
          <Route path="/industrials/data-engineering/data-infrastructure-architecture" element={<IndustrialsDataInfrastructureArchitecturePage />} />
          <Route path="/industrials/data-engineering/data-pipelines-integration" element={<IndustrialsDataPipelinesIntegrationPage />} />
          <Route path="/industrials/data-engineering/apis-interoperability" element={<IndustrialsAPIsInteroperabilityPage />} />
          <Route path="/industrials/ai-development/apis-interoperability" element={<IndustrialsAPIsInteroperabilityPage />} />
          <Route path="/industrials/data-engineering/data-governance-quality" element={<IndustrialsDataGovernanceQualityPage />} />
          <Route path="/industrials/data-engineering/visualization-insights" element={<IndustrialsVisualizationInsightsPage />} />

          {/* Selected Cases (PDF cases) – shared route for mining + industrials */}
          <Route path="/:vertical/selected-cases/:slug" element={<SelectedCasePage />} />

          {/* Industries — clean top-level URLs (was /industries/:slug) */}
          <Route path="/cement" element={<IndustryPage />} />
          <Route path="/energy" element={<IndustryPage />} />
          <Route path="/oil-gas" element={<IndustryPage />} />
          <Route path="/manufacturing" element={<IndustryPage />} />
          <Route path="/machinery" element={<IndustryPage />} />
          <Route path="/construction" element={<IndustryPage />} />

          {/* Legacy /industries/* redirects → new short URLs */}
          <Route path="/industries" element={<Navigate to="/" replace />} />
          <Route path="/industries/mining" element={<Navigate to="/mining" replace />} />
          <Route path="/industries/cement-materials" element={<Navigate to="/cement" replace />} />
          <Route path="/industries/energy-power" element={<Navigate to="/energy" replace />} />
          <Route path="/industries/oil-gas" element={<Navigate to="/oil-gas" replace />} />
          <Route path="/industries/manufacturing" element={<Navigate to="/manufacturing" replace />} />
          <Route path="/industries/machinery-oem" element={<Navigate to="/machinery" replace />} />
          <Route path="/industries/construction" element={<Navigate to="/construction" replace />} />

          {/* Offerings & Capabilities */}
          <Route path="/offerings" element={<OfferingsPage />} />
          <Route path="/capabilities" element={<CapabilitiesPage />} />

          {/* Dedicated booking page */}
          <Route path="/book-a-meeting" element={<BookMeetingPage />} />
          </Routes>
        </Suspense>
      </BrowserRouter>
    </div>
  );
}

export default App;
